import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { 
  Menu, 
  Home, 
  Info, 
  Calendar, 
  Mail, 
  LogIn, 
  LogOut, 
  User, 
  Settings,
  ChevronDown,
  Building2,
  Search,
  X,
  Image,
  FileText
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/xm3vjt0f_TCF_logo.jpg";

const committees = [
  { id: "mosque-dawah", name: "Mosque and Da'wah" },
  { id: "project-maintenance", name: "Project and Maintenance" },
  { id: "welfare-publicity", name: "Welfare and Publicity" },
  { id: "finance-investment", name: "Finance and Investment" },
  { id: "security-ethic", name: "Security and Ethics" },
];

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const NavLinks = ({ mobile = false, onClose }) => (
    <>
      <Link 
        to="/" 
        className={`flex items-center gap-2 font-medium ${mobile ? 'py-3 px-4 hover:bg-primary-50 rounded-xl text-gray-700' : 'text-gray-600 hover:text-primary-900 transition-colors'}`}
        onClick={onClose}
        data-testid="nav-home"
      >
        <Home className="w-4 h-4" />
        Home
      </Link>
      <Link 
        to="/about" 
        className={`flex items-center gap-2 font-medium ${mobile ? 'py-3 px-4 hover:bg-primary-50 rounded-xl text-gray-700' : 'text-gray-600 hover:text-primary-900 transition-colors'}`}
        onClick={onClose}
        data-testid="nav-about"
      >
        <Info className="w-4 h-4" />
        About
      </Link>
      
      {mobile ? (
        <div className="py-2">
          <p className="px-4 text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Committees</p>
          {committees.map((c) => (
            <Link
              key={c.id}
              to={`/committee/${c.id}`}
              className="block py-2.5 px-6 hover:bg-primary-50 rounded-xl text-gray-700 font-medium"
              onClick={onClose}
              data-testid={`nav-committee-${c.id}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1.5 text-gray-600 hover:text-primary-900 transition-colors font-medium" data-testid="nav-committees-dropdown">
            <Building2 className="w-4 h-4" />
            Committees
            <ChevronDown className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60 p-2 rounded-xl">
            {committees.map((c) => (
              <DropdownMenuItem key={c.id} asChild className="rounded-lg">
                <Link to={`/committee/${c.id}`} className="font-medium" data-testid={`nav-committee-${c.id}`}>{c.name}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Link 
        to="/media-gallery" 
        className={`flex items-center gap-2 font-medium ${mobile ? 'py-3 px-4 hover:bg-primary-50 rounded-xl text-gray-700' : 'text-gray-600 hover:text-primary-900 transition-colors'}`}
        onClick={onClose}
        data-testid="nav-media"
      >
        Media Gallery
      </Link>

      <Link 
        to="/events" 
        className={`flex items-center gap-2 font-medium ${mobile ? 'py-3 px-4 hover:bg-primary-50 rounded-xl text-gray-700' : 'text-gray-600 hover:text-primary-900 transition-colors'}`}
        onClick={onClose}
        data-testid="nav-events"
      >
        <Calendar className="w-4 h-4" />
        Events
      </Link>
      <Link 
        to="/contact" 
        className={`flex items-center gap-2 font-medium ${mobile ? 'py-3 px-4 hover:bg-primary-50 rounded-xl text-gray-700' : 'text-gray-600 hover:text-primary-900 transition-colors'}`}
        onClick={onClose}
        data-testid="nav-contact"
      >
        <Mail className="w-4 h-4" />
        Contact
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-stone-200/50 bg-white/80 backdrop-blur-lg">
      <div className="container-custom">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <img src={LOGO_URL} alt="TCF Logo" className="h-12 w-12 rounded-full object-cover shadow-md ring-2 ring-primary-100 group-hover:ring-primary-200 transition-all" />
            <div className="hidden sm:block">
              <h1 className="font-heading text-lg font-semibold text-primary-900 tracking-tight">The Companions Foundation</h1>
              <p className="text-xs text-gray-500 font-medium">Service to Humanity</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <NavLinks />
          </nav>

          {/* Search & Auth Section */}
          <div className="flex items-center gap-3">
            {/* Search Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSearchOpen(true)}
              className="text-gray-500 hover:text-primary-900 hover:bg-primary-50 rounded-full"
              data-testid="search-btn"
            >
              <Search className="w-5 h-5" />
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary-900" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                          <Settings className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/media-gallery" className="flex items-center gap-2" data-testid="media-gallery-link">
                          <Image className="w-4 h-4" />
                          Media Gallery
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/committee/mosque-dawah" className="flex items-center gap-2" data-testid="committee-posts-link">
                          <FileText className="w-4 h-4" />
                          Committee Posts
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" asChild data-testid="login-btn">
                  <Link to="/login">
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Link>
                </Button>
                <Button className="btn-primary" asChild data-testid="register-btn">
                  <Link to="/register">Register</Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <img src={LOGO_URL} alt="TCF Logo" className="h-8 w-8 rounded-full" />
                      <span className="font-heading font-bold text-primary-900">TCF</span>
                    </div>
                  </div>
                  <nav className="flex flex-col gap-1">
                    <NavLinks mobile onClose={() => setMobileOpen(false)} />
                  </nav>
                  {!user && (
                    <div className="mt-auto pt-6 border-t">
                      <Button className="w-full btn-primary mb-2" asChild>
                        <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
                      </Button>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>Register</Link>
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20" onClick={() => setSearchOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSearch} className="p-4">
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search posts, events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-grow border-none shadow-none text-lg focus-visible:ring-0"
                  autoFocus
                  data-testid="search-input"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </form>
            <div className="border-t px-4 py-3 text-sm text-gray-500">
              Press Enter to search or ESC to close
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
